<template>
  <v-container fluid class="pa-0">
    <v-row justify="space-between" class="flex-nowrap">

      <v-col cols="auto" class="d-flex align-center flex-shrink-0">
        <div class="d-flex flex-column align-center">
          <v-menu offset-y left>
            <template #activator="{ on, attrs }">
              <v-btn icon v-bind="attrs" v-on="on">
                <v-icon color="white">mdi-account-circle</v-icon>
              </v-btn>
            </template>
            <v-list dense>
              <v-list-item
                v-for="(item, i) in mainMenuItems"
                :key="i"
                @click="handleMenuItemClick(item)"
                link
              >
                <v-list-item-title>{{ item.title }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>

          <v-app-bar-nav-icon
            v-if="isLoggedIn"
            color="white"
            @click="$router.push({ name: 'selectCompetition' })"
          />
        </div>

        <v-img
          max-height="70"
          max-width="70"
          contain
          :src="require('@/assets/sport1.png')"
          @click="$router.push('/')"
          class="clickable ml-2 d-none d-sm-block"
        />
      </v-col>

      <v-col
        v-if="hasCompetitions"
        class="text-center mx-2 mt-2"
        style="min-width: 100px;"
      >
        <div class="d-flex">
          <div class="text-h6 text-truncate" :title="selectedCompetition ? selectedCompetition.name : ''">
            {{ selectedCompetition ? selectedCompetition.name : (visibleCompetitions.length ? visibleCompetitions[0].name : $t('shared.noActiveCompetitions')) }}
          </div>
          <v-menu offset-y>
            <template #activator="{ on, attrs }">
              <v-btn icon small v-bind="attrs" v-on="on">
                <v-icon color="white" small>mdi-chevron-down</v-icon>
              </v-btn>
            </template>
            <v-list dense>
              <v-list-item
                v-for="(competition, index) in visibleCompetitions"
                :key="index"
                @click="selectCompetition(competition)"
                link
              >
                <v-list-item-title>{{ competition.name }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>

        <div class="d-flex">
          <div class="text-subtitle-1 text-truncate" :title="selectedSportType ? selectedSportType.name : ''">
            {{ selectedSportType ? selectedSportType.name : $t('shared.noActiveSportTypes') }}
          </div>
          <v-menu offset-y>
            <template #activator="{ on, attrs }">
              <v-btn icon small v-bind="attrs" v-on="on">
                <v-icon color="white" small>mdi-chevron-down</v-icon>
              </v-btn>
            </template>
            <v-list dense>
              <v-list-item
                v-for="(type, index) in availableSportTypes"
                :key="index"
                @click="selectSportType(type)"
                link
              >
                <v-list-item-title>{{ type.name }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
      </v-col>

      <v-col cols="auto" class="d-flex align-center justify-end flex-shrink-0">
        <div v-if="hasCompetitions && selectedCompetition" class="d-none d-lg-block text-right mr-4">
          <div class="text-subtitle-2">{{ selectedCompetition.startDate }}</div>
          <div class="text-caption">{{ selectedCompetition.locationName }}</div>
        </div>

        <div class="d-none d-sm-flex align-center flex-wrap" style="max-width: 100px;">
          <v-btn
            v-for="social in socialLinks"
            :key="social.name"
            :href="social.url"
            small
            color="white"
            icon
            target="_blank"
          >
            <v-icon>mdi-{{ social.name }}</v-icon>
          </v-btn>
        </div>

        <div class="d-none d-md-flex align-center justify-center text-center mx-2" style="min-width: 85px;">
          <!-- <v-progress-circular indeterminate size="20" width="2" v-if="!weatherData"></v-progress-circular> -->
          <p v-if="weatherData" class="ma-0 text-subtitle-2">{{ selectedCompetition.locationName }} {{ Math.round(weatherData.main.temp) }}°C</p>
        </div>

        <div class="d-flex align-center">
          <button
            v-for="(language, index) in languageOptions"
            :key="index"
            :class="{ 'ml-2': index }"
            :style="language.style"
            @click="switchLanguage(language.code)"
            class="d-flex"
          >
            <v-img
              :height="language.height"
              :width="language.width"
              :src="require(`@/assets/${language.code}-flag.png`)"
              :alt="language.alt"
              class="clickable language-flag"
            />
          </button>
        </div>
      </v-col>
    </v-row>

    <v-dialog v-model="registrationDialog" max-width="450px" persistent>
      <v-card>
        <v-card-title>
          <span class="text-h5">{{ $t('shared.userRegistration') }}</span>
        </v-card-title>
        <v-card-text>
          <v-form @submit.prevent="registerUser">
            <v-text-field v-model="firstName" :label="$t('user.firstName')" required prepend-icon="mdi-account"></v-text-field>
            <v-text-field v-model="secondName" :label="$t('user.lastName')" required prepend-icon="mdi-account-outline"></v-text-field>
            <v-text-field v-model="username" :label="$t('user.username')" required prepend-icon="mdi-account-box"></v-text-field>
            <v-text-field
              v-model="password"
              :label="$t('user.password')"
              required
              prepend-icon="mdi-lock"
              :append-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
              :type="showPassword ? 'text' : 'password'"
              @click:append="showPassword = !showPassword"
            ></v-text-field>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn text @click="cancelRegistration">{{ $t('shared.cancel') }}</v-btn>
              <v-btn type="submit" color="primary" depressed>{{ $t('shared.register') }}</v-btn>
            </v-card-actions>
          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="registrationSuccessDialog" max-width="400px">
      <v-card>
        <v-card-title class="text-h5 success--text">Успішно!</v-card-title>
        <v-card-text>Зв'яжіться з адміном для завершення реєстрації.</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="closeRegistrationSuccessDialog">OK</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="loginSuccessDialog" max-width="400px">
      <v-card>
        <v-card-title class="text-h5 success--text">Успішно!</v-card-title>
        <v-card-text>Ви успішно увійшли в систему.</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="closeLoginSuccessDialog">OK</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </v-container>
</template>

<script>
import axios from 'axios';
import { EventBus } from '../../eventBus';

export default {
  props: {
    isLoggedIn: {
      type: Boolean,
      default: false
    }
  },
  data: function () {
    return {
      competitions: [],
      hasCompetitions: false,
      selectedCompetition: null,
      loginUsername: '',
      loginPassword: '',
      loginDialog: false,
      loginSuccessDialog: false,
      loginError: null,
      username: '',
      firstName: '',
      secondName: '',
      password: '',
      showPassword: false,
      registrationDialog: false,
      registrationSuccessDialog: false,
      activeLang: null,
      weatherData: null,
      apiKey: '4a697c396f11e7450074d7f1e5b233ec', // Note: It's better to store API keys in environment variables
      units: 'metric',
      accessToken: '',
      sportTypes: [
        { name: 'Подолання 100м смуги з перешкодами', value: 'HUNDRED_METER' },
        { name: 'Штурмова драбина', value: 'ASSAULT_LADDER' },
        { name: 'Двоборство', value: 'DUELING' },
      ],
      selectedSportType: null,
      selectedCompetitionReference: localStorage.getItem('selectedCompetitionReference') || null,
      selectedSportTypeValue: localStorage.getItem('selectedSportTypeValue') || null,
    }
  },
  mounted() {
    this.fetchCompetitions().then(() => {
      if (this.selectedCompetitionReference) {
        this.selectedCompetition = this.competitions.find(competition => competition.competitionReference === this.selectedCompetitionReference);
        this.fetchWeather();
      }
      if (this.selectedSportTypeValue) {
        this.selectedSportType = this.sportTypes.find(type => type.value === this.selectedSportTypeValue);
      }
    });
    this.fetchSocialLinks();
  },
  computed: {
    availableSportTypes() {
      if (!this.selectedCompetition || !this.selectedCompetition.competitionTypes) {
        return this.sportTypes;
      }
      const hiddenTypes = ['DUELING']; // TODO: remove when public API is fixed
      const competitionSportTypes = this.selectedCompetition.competitionTypes.map(ct => ct.sportType);
      return this.sportTypes.filter(type =>
          competitionSportTypes.includes(type.value) && !hiddenTypes.includes(type.value)
      );
    },
    mainMenuItems() {
      const isLoggedIn = this.isLoggedIn;
      const items = [
        { title: this.$t(`shared.profile`), show: isLoggedIn, action: () => this.$router.push('/profile') },
        { title: this.$t(`shared.commandProtocol`), show: isLoggedIn, action: () => this.$router.push('/protocol') },
        { title: this.$t(`shared.userRegistration`), show: !isLoggedIn, action: this.openRegistrationDialog },
        { title: this.$t(`shared.userLogin`), show: !isLoggedIn, action: () => this.$emit('toggleLoginDialog', true) },
        { title: this.$t(`shared.usersBase`), show: isLoggedIn, action: () => this.$router.push('/admin') },
        { title: this.$t(`shared.userLogout`), show: isLoggedIn, action: () => this.$emit('logoutUser') },
      ];
      return items.filter(({ show }) => show);
    },
    socialLinks() {
      // @TODO these to be provided by backend
      const items = [
        { name: 'telegram', url: 'https://google.com.ua' },
        { name: 'facebook', url: '' },
        { name: 'twitter', url: '' },
        { name: 'youtube', url: '' },
        { name: 'instagram', url: '' },
        { name: 'web', url: '' },
      ];
      return items;
    },
    visibleCompetitions() {
      return this.competitions.filter(competition => competition.show);
    },
    languageOptions() {
      const activeLang = this.activeLang;
      const defaultOptions = { width: '33', height: '20' }
      return [
        { code: 'uk', alt: 'Українська' },
        { code: 'en', alt: 'English language' }
      ].map((item) => ({ ...item, ...defaultOptions, style: { 'border': `2px solid ${activeLang === item.code ? 'white' : 'transparent'}` } }));
    }
  },
  watch: {
    activeLang: {
      handler(v) {
        if (v) {
          this.$i18n.locale = v;
        } else {
          this.switchLanguage(localStorage.getItem('dsns-competitions-lang') || 'uk');
        }
      },
      immediate: true
    }
  },
  methods: {
    handleMenuItemClick(item) {
      if (item.action) {
        item.action();
      }
    },
    async fetchSocialLinks() {
      try {
        // const response = await axios.get('public/social-network');
        // this.socialLinks = response.data.map(link => ({
        //   name: link.linkType.toLowerCase(),
        //   url: link.link
        // }));
      } catch (error) {
        console.error('Error fetching social links:', error);
      }
    },
    async fetchCompetitions() {
      try {
        const response = await axios.get('public/competitions/available');
        this.competitions = response.data;
        this.hasCompetitions = this.competitions.length > 0;

        if (this.competitions.length > 0) {
          if (!this.selectedCompetitionReference) {
            this.selectCompetition(this.competitions[0]);
            const firstSportType = this.competitions[0].competitionTypes.find(ct => ct.sportType === 'HUNDRED_METER');
            if (firstSportType) {
              this.selectSportType({ name: 'Подолання 100м смуги з перешкодами', value: 'HUNDRED_METER' });
            }
          }
        } else {
          localStorage.removeItem('selectedCompetitionReference');
          localStorage.removeItem('selectedSportTypeValue');
          this.selectedCompetition = null;
          this.selectedSportType = null;
        }
      } catch (error) {
        console.error('Error fetching competitions:', error);
      }
    },
    async fetchWeather() {
      if (!this.selectedCompetition || !this.selectedCompetition.locationName) return;
      try {
        const city = this.selectedCompetition.locationName
            .replace(/^м\.\s*/, '')
            .replace(/\s*-\s*/g, '-')
            .trim();
        const response = await axios.get(`public/weather?city=${city}`);
        this.weatherData = response.data;
      } catch (error) {
        console.error('Помилка отримання погодних даних:', error);
      }
    },
    selectCompetition(competition) {
      this.selectedCompetition = competition;
      localStorage.setItem('selectedCompetitionReference', competition.competitionReference);
      EventBus.$emit('competitionChanged', competition.competitionReference);
      this.fetchWeather();

      const currentSportTypeValue = localStorage.getItem('selectedSportTypeValue');
      const correspondingSportType = competition.competitionTypes.find(ct => ct.sportType === currentSportTypeValue);

      if (correspondingSportType) {
        const sportType = this.sportTypes.find(type => type.value === correspondingSportType.sportType);
        if (sportType) this.selectSportType(sportType);
      } else if (competition.competitionTypes.length > 0) {
        const firstSportType = competition.competitionTypes[0];
        const sportType = this.sportTypes.find(type => type.value === firstSportType.sportType);
        if (sportType) this.selectSportType(sportType);
      }
    },
    selectSportType(type) {
      this.selectedSportType = type;
      localStorage.setItem('selectedSportTypeValue', type.value);
      EventBus.$emit('sportTypeChanged', type.value);
      const selectedType = this.selectedCompetition.competitionTypes.find(ct => ct.sportType === type.value);
      if (selectedType) {
        localStorage.setItem('selectedSportTypeReference', selectedType.reference);
      }
    },
    switchLanguage(lang) {
      this.activeLang = lang;
      localStorage.setItem('dsns-competitions-lang', lang);
    },
    openRegistrationDialog() {
      this.registrationDialog = true;
    },
    cancelRegistration() {
      this.registrationDialog = false;
    },
    showAdminMessage() {
      this.registrationDialog = false;
      this.registrationSuccessDialog = true;
    },
    closeRegistrationSuccessDialog() {
      this.registrationSuccessDialog = false;
    },
    closeLoginSuccessDialog() {
      this.loginSuccessDialog = false;
    },
    async registerUser() {
      try {
        const response = await axios.post('public/auth/registration', {
          username: this.username,
          password: this.password,
          firstName: this.firstName,
          secondName: this.secondName,
        });
        console.log('Успішна реєстрація:', response.data);
        this.showAdminMessage();
      } catch (error) {
        console.error('Помилка реєстрації:', error);
      }
    },
  }
}
</script>

<style scoped>
.clickable {
  cursor: pointer;
}

.language-flag {
  border-radius: 3px;
  transition: transform 0.2s ease-in-out;
}

.language-flag:hover {
  transform: scale(1.1);
}

.v-input__icon--append .v-icon {
  cursor: pointer;
}
</style>
